import { FormEvent, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Button, Container, Grid, TextField, Typography, Link } from '@mui/material'

import { getConfiguration } from '../../clients/configuration'
import { AuthenticationApi, ResponseError } from '../../clients'
import { useAppDispatch } from '../../redux/hooks'
import { AppAlert } from '../../components/AppAlerts/AppAlerts'
import { addAlertWithTimeout } from '../../redux/features/alerts/alertsHelpers'
import { RecaptchaService } from '../../services/recaptchaService'

export const CreateAccount = () => {

    const dispatch = useAppDispatch()
    const [email, setEmail] = useState<string>('')
    const [emailError, setEmailError] = useState<string>('')
    const [submitting, setSubmitting] = useState<boolean>(false)

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (handleEmailBlur()) {
            return
        }
        const recaptchaService = new RecaptchaService()
        const token = await recaptchaService.getToken()
        setSubmitting(true)
        const config = await getConfiguration()
        try {
            await new AuthenticationApi(config).createAccount({
                createAccountRequest: {
                    username: email,
                    recaptchaToken: token,
                }
            })
        } catch (err) {
            const error = err as ResponseError
            const res = error.response
            const text = await res.text().catch(() => null)
            if (text === 'An account with the given email already exists') {
                const alert = new AppAlert(
                    'error',
                    'That email is already registered'
                )
                setSubmitting(false)
                return addAlertWithTimeout(dispatch, alert)
            }
            const alert = new AppAlert(
                'error',
                'Something went wrong. Please try again later.'
            )
            addAlertWithTimeout(dispatch, alert)
        }
        setSubmitting(false)
    }

    const handleEmailFocus = () => {
        setEmailError('')
    }

    const handleEmailBlur = (): string => {
        const emailRegex = new RegExp(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g)
        if (!email.match(emailRegex)?.length) {
            const error = 'Invalid email address'
            setEmailError(error)
            return error
        }
        return ''
    }

    return (
        <Container component='div' maxWidth='sm'>
            <Typography component="h1" variant="h5" sx={{ mt: 8, textAlign: 'center' }}>
                Sign Up
            </Typography>
            <Box component='form' onSubmit={handleSubmit} sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            required
                            fullWidth
                            value={email}
                            error={!!emailError}
                            helperText={emailError}
                            onFocus={handleEmailFocus}
                            onBlur={handleEmailBlur}
                            onChange={(e) => setEmail(e.target.value)}
                            label='Email'
                        />
                    </Grid>
                </Grid>
                <Grid container sx={{ mt: 1 }}>
                    <Grid size={{ xs: 12 }} display='flex' justifyContent='end'>
                        <RouterLink to='/auth/sign-in'><Link component='p'>Already have an account? Sign in</Link></RouterLink>
                    </Grid>
                </Grid>
                <Button disabled={submitting || !!emailError || !email} type='submit' fullWidth variant="contained" color="primary" sx={{ mt: 2, mb: 2 }}>Sign Up</Button>
                <Box sx={{ textAlign: 'center', my: 1, mb: 0 }}>
                    <Typography variant='caption'>
                        This site is protected by reCAPTCHA and the Google&nbsp;
                        <a href="https://policies.google.com/privacy">Privacy Policy</a> and&nbsp;
                        <a href="https://policies.google.com/terms">Terms of Service</a> apply.
                    </Typography>
                </Box>
            </Box>
        </Container>
    )
}